import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Reveal from './Reveal';

const STATS = [
  { value: '300M+', label: 'Influencer Profiles' },
  { value: '6B+', label: 'Data Points' },
  { value: '50K+', label: 'Brands Served' },
  { value: '120+', label: 'Countries' },
];

type LandingMarketingHeroProps = {
  /** `marketing` = full landing hero with CTAs + stats (always on the dark animated canvas). `auth` = reserved for future use / responsive slate+dark pairs. */
  variant?: 'marketing' | 'auth';
  className?: string;
};

/**
 * Shared hero headline for the landing page. Marketing copy always uses light-on-dark typography
 * because the hero sits on the purple abstract background even when the user toggles global “light” theme.
 */
export default function LandingMarketingHero({ variant = 'marketing', className = '' }: LandingMarketingHeroProps) {
  const isAuth = variant === 'auth';
  /** Dark purple hero canvas — always for `marketing`; auth split layout would use responsive colors if revived. */
  const onDarkHero = !isAuth;

  return (
    <div
      className={`mx-auto max-w-4xl px-6 ${isAuth ? 'text-center lg:text-left lg:mx-0 lg:max-w-xl lg:px-8' : 'text-center'} ${className}`}
    >
      <Reveal direction="up" delay={0}>
        <div
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${
            isAuth ? 'mb-6' : 'mb-8'
          } ${
            onDarkHero
              ? 'border-white/10 bg-white/5 text-gray-300 shadow-none'
              : 'border-slate-200/90 bg-white/70 text-slate-600 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-gray-400 dark:shadow-none'
          }`}
        >
          <span
            className={`h-2 w-2 animate-pulse rounded-full ${onDarkHero ? 'bg-emerald-400' : 'bg-emerald-500 dark:bg-emerald-400'}`}
          />
          Now with AI-powered script generator and checker
        </div>
      </Reveal>

      <Reveal direction="up" delay={80}>
        <p
          className={`mb-4 font-semibold tracking-wide ${
            onDarkHero ? 'text-violet-300' : 'text-violet-700 dark:text-violet-400'
          } ${isAuth ? 'text-base md:text-lg' : 'text-lg md:text-xl'}`}
        >
          Your Influencer Marketing AI Command Center
        </p>
      </Reveal>

      <Reveal direction="up" delay={140}>
        <h1
          className={`font-extrabold leading-tight ${onDarkHero ? 'text-white' : 'text-slate-900 dark:text-white'} ${
            isAuth ? 'mb-4 text-3xl sm:text-4xl lg:text-5xl' : 'mb-6 text-5xl md:text-7xl'
          }`}
        >
          <span
            className={
              onDarkHero
                ? 'bg-gradient-to-r from-violet-400 via-indigo-400 to-blue-400 bg-clip-text text-transparent'
                : 'bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent dark:from-violet-500 dark:via-indigo-500 dark:to-blue-500'
            }
          >
            Advanced Influencer AI
          </span>
          <br />
          <span className={onDarkHero ? 'text-white' : 'text-slate-800 dark:text-white'}>Management Platform</span>
        </h1>
      </Reveal>

      <Reveal direction="up" delay={220}>
        <p
          className={`mx-auto max-w-2xl leading-relaxed ${
            onDarkHero ? 'text-gray-300' : 'text-slate-600 dark:text-gray-400'
          } ${isAuth ? 'mb-0 text-base md:text-lg lg:mx-0' : 'mb-10 text-lg md:text-xl'}`}
        >
          Discover 300M+ influencers, analyze real-time social data, and launch campaigns across Instagram, TikTok,
          YouTube & more — all powered by AI.
        </p>
      </Reveal>

      {!isAuth ? (
        <>
          <Reveal direction="up" delay={300}>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-blue-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-violet-500/30 transition-opacity hover:opacity-90"
              >
                Start Free Trial
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-lg border border-white/15 bg-white/5 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-white/10"
              >
                Book Demo
              </Link>
            </div>
          </Reveal>

          <div className="mt-16 flex flex-wrap justify-center gap-14 border-t border-white/10 pt-10">
            {STATS.map((stat, index) => (
              <Reveal key={index} direction="up" delay={400 + index * 90}>
                <div className="text-center">
                  <div className="text-4xl font-bold bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-sm text-gray-400">{stat.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
